import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircle2,
  Copy,
  Mail,
  Calendar,
  FileText,
  ArrowLeft,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getApplicationByFolio } from "@/lib/api";

export default function Confirmacion() {
  const { folio } = useParams();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!folio) return;
    getApplicationByFolio(folio)
      .then(setApp)
      .catch(() => toast.error("No se pudo cargar el folio"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folio]);

  const copyFolio = async () => {
    try {
      await navigator.clipboard.writeText(folio || "");
      toast.success("Folio copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-eje/[0.02]">
      <Header variant="light" />

      <main className="container-narrow py-16 flex-1">
        {loading ? (
          <div className="text-center py-20 text-slate-500">Cargando…</div>
        ) : !app ? (
          <div className="text-center py-20">
            <p className="text-slate-600 mb-4">Folio no encontrado.</p>
            <Link to="/inscripcion" className="btn-primary-eje">
              Volver a inscripción
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-10 animate-fade-in-up">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <div className="overline text-eje mb-2">Inscripción confirmada</div>
              <h1
                className="font-serif text-4xl md:text-5xl text-eje mb-4"
                data-testid="confirmacion-title"
              >
                Hemos recibido tu solicitud
              </h1>
              <p className="text-slate-600 max-w-xl mx-auto">
                Tu inscripción a la Convocatoria Posdoctorado y Estancias 2026
                de la Escuela Judicial Electoral ha sido registrada
                exitosamente.
              </p>
            </div>

            <div
              className="card-eje p-8 md:p-10 mb-8 animate-fade-in-up"
              style={{ animationDelay: "120ms" }}
            >
              <div className="overline text-slate-500 mb-2">Folio asignado</div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div
                  className="font-serif text-4xl md:text-5xl text-eje tracking-wide"
                  data-testid="enrollment-folio-number"
                >
                  {app.folio}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copyFolio}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-eje text-eje rounded-sm hover:bg-eje hover:text-white transition-colors text-sm"
                    data-testid="copy-folio-button"
                  >
                    <Copy className="w-4 h-4" /> Copiar
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors text-sm"
                    data-testid="print-button"
                  >
                    <Printer className="w-4 h-4" /> Imprimir
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200 mt-8">
                <Info label="Aspirante" value={`${app.nombre} ${app.apellido_paterno} ${app.apellido_materno || ""}`.trim()} />
                <Info label="Correo electrónico" value={app.email} />
                <Info label="Modalidad" value={app.modalidad.replace(/_/g, " ")} />
                <Info label="Línea" value={(app.linea_investigacion || "—").replace(/_/g, " ")} />
                <Info
                  label="Documentos cargados"
                  value={`${app.documents?.length || 0}`}
                />
                <Info
                  label="Fecha de envío"
                  value={
                    app.submitted_at
                      ? new Date(app.submitted_at).toLocaleString("es-MX")
                      : "—"
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              <NextCard
                icon={<Mail />}
                title="Guarda tu folio"
                text="Este número es tu única referencia para cualquier solicitud o aclaración posterior."
              />
              <NextCard
                icon={<Calendar />}
                title="Resultados"
                text="Los resultados preliminares se publicarán a partir del 6 de agosto de 2026 (09:00 hrs CDMX). Los resultados definitivos se notificarán a más tardar el 20 de agosto."
              />
              <NextCard
                icon={<FileText />}
                title="Imprime esta página"
                text="Conserva el comprobante de inscripción con tu folio y la fecha de envío como respaldo."
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to={`/comprobante/${app.folio}`}
                className="inline-flex items-center justify-center gap-2 btn-primary-eje"
                data-testid="view-comprobante-button"
              >
                <Printer className="w-4 h-4" /> Ver comprobante imprimible
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 btn-secondary-eje"
                data-testid="back-home-button"
              >
                <ArrowLeft className="w-4 h-4" /> Volver al inicio
              </Link>
            </div>

            <div className="mt-12 text-center text-sm text-slate-500">
              Para cualquier solicitud de revisión escribe a{" "}
              <a
                href="mailto:pei.eje@te.gob.mx"
                className="text-eje underline hover:text-gold-600"
              >
                pei.eje@te.gob.mx
              </a>
              .
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-white p-4">
      <div className="overline text-slate-500 mb-1">{label}</div>
      <div className="text-sm text-slate-800 capitalize break-words">{value || "—"}</div>
    </div>
  );
}

function NextCard({ icon, title, text }) {
  return (
    <div className="card-eje p-5">
      <div className="text-eje mb-3 [&>svg]:w-5 [&>svg]:h-5">{icon}</div>
      <div className="font-serif text-lg text-eje mb-1">{title}</div>
      <p className="text-xs text-slate-600 leading-relaxed">{text}</p>
    </div>
  );
}
